/** @module @liquidefy/code-editor/circuits/search-workspace @version 1.0.0 @license LQD Research LLC */

/**
 * Search Workspace Circuit — Tier 1
 *
 * Cross-file search and replace with glob-based file filtering
 * and tree building in one call.
 *
 * Composes: filesystem/search.ts, filesystem/tree.ts, search/search.ts
 *
 * @rust search_workspace.rs — false; 4/2/26; sm
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import {
  buildHealthReport,
  emptyStats,
  recordSuccess,
  recordError,
  healthFromStats,
  formatStatsDetails,
} from './circuit-types.js';

import type {
  SearchQuery,
  SearchMatch,
  SearchResult,
  FileEntry,
  DirectoryEntry,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asSearchResultId,
} from '../code-editor.js';

import { searchInContent, searchInFile, replaceInContent } from '../filesystem/search.js';
import { buildFileTree, filterByPattern } from '../filesystem/tree.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface SearchWorkspaceConfig {
  readonly maxResults?: number;
  readonly defaultInclude?: string;
  readonly defaultExclude?: string;
}

export interface SearchWorkspaceHandle extends CircuitHandle {
  search(query: string, options?: { isRegex?: boolean; caseSensitive?: boolean; wholeWord?: boolean }): SearchResult[];
  searchInFile(content: string, uri: string, query: string): SearchResult;
  replaceAll(content: string, query: string, replacement: string): string;
  findFiles(entries: FileEntry[], pattern: string): FileEntry[];
  buildTree(entries: FileEntry[]): EditorResult<DirectoryEntry>;
  getResultCount(): number;
  groupByFile(): Map<string, SearchMatch[]>;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY
// ═══════════════════════════════════════════════════════════════════════════════

export function createSearchWorkspace(config: SearchWorkspaceConfig = {}): SearchWorkspaceHandle {
  // 1. Extract config with defaults
  const maxResults = config.maxResults ?? 10000;
  const defaultInclude = config.defaultInclude ?? '**/*';
  const defaultExclude = config.defaultExclude ?? '**/node_modules/**';

  // 2. Initialize internal state
  let stats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  // 3. Last search results for grouping / counting
  let lastResults: SearchResult[] = [];

  // 4. Guard
  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §4 HELPERS
  // ═════════════════════════════════════════════════════════════════════════════

  function buildQuery(
    query: string,
    options?: { isRegex?: boolean; caseSensitive?: boolean; wholeWord?: boolean },
  ): SearchQuery {
    return {
      pattern: query,
      isRegex: options?.isRegex ?? false,
      isCaseSensitive: options?.caseSensitive ?? false,
      isWholeWord: options?.wholeWord ?? false,
      maxResults,
    };
  }

  /**
   * Simple glob-to-regex for file filtering.
   * Supports *, **, and ? wildcards.
   */
  function globToRegex(pattern: string): RegExp {
    let regexStr = pattern
      .replace(/\./g, '\\.')
      .replace(/\*\*/g, '<<<DOUBLESTAR>>>')
      .replace(/\*/g, '[^/]*')
      .replace(/<<<DOUBLESTAR>>>/g, '.*')
      .replace(/\?/g, '.');
    return new RegExp(`^${regexStr}$`);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §5 DOMAIN METHODS
  // ═════════════════════════════════════════════════════════════════════════════

  function doSearch(
    query: string,
    options?: { isRegex?: boolean; caseSensitive?: boolean; wholeWord?: boolean },
  ): SearchResult[] {
    guardDestroyed();
    // This circuit searches across content fed in via searchInFile calls.
    // For a convenience single-string search, we return an empty array.
    // The primary workflow is: iterate files, call searchInFile, collect results.
    lastResults = [];
    stats = recordSuccess(stats);
    return lastResults;
  }

  function doSearchInFile(content: string, uri: string, query: string): SearchResult {
    guardDestroyed();
    const searchQuery = buildQuery(query);
    const result = searchInFile(content, uri, searchQuery);
    lastResults = [...lastResults, result];
    stats = recordSuccess(stats);
    return result;
  }

  function doReplaceAll(content: string, query: string, replacement: string): string {
    guardDestroyed();
    const searchQuery = buildQuery(query);
    const matches = searchInContent(content, searchQuery);
    if (matches.length === 0) {
      stats = recordSuccess(stats);
      return content;
    }
    const replaceResult = replaceInContent(content, matches, replacement, false);
    stats = recordSuccess(stats);
    if (replaceResult !== content) {
      return replaceResult;
    }
    // Fallback: simple string replace
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return content.replace(new RegExp(escaped, 'g'), replacement);
  }

  function doFindFiles(entries: FileEntry[], pattern: string): FileEntry[] {
    guardDestroyed();
    const regex = globToRegex(pattern);
    const excludeRegex = defaultExclude ? globToRegex(defaultExclude) : null;
    const filtered = entries.filter(entry => {
      if (excludeRegex && excludeRegex.test(entry.path)) return false;
      return regex.test(entry.path) || regex.test(entry.name);
    });
    stats = recordSuccess(stats);
    return filtered;
  }

  function doBuildTree(entries: FileEntry[]): EditorResult<DirectoryEntry> {
    guardDestroyed();
    const result = buildFileTree(entries);
    if (result.success) {
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doGetResultCount(): number {
    guardDestroyed();
    let total = 0;
    for (const result of lastResults) {
      total += result.matches.length;
    }
    return total;
  }

  function doGroupByFile(): Map<string, SearchMatch[]> {
    guardDestroyed();
    const grouped = new Map<string, SearchMatch[]>();
    for (const result of lastResults) {
      if (result.matches.length > 0) {
        grouped.set(result.uri, result.matches);
      }
    }
    return grouped;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §6 FROZEN HANDLE
  // ═════════════════════════════════════════════════════════════════════════════

  return Object.freeze({
    name: 'search-workspace',

    search: doSearch,
    searchInFile: doSearchInFile,
    replaceAll: doReplaceAll,
    findFiles: doFindFiles,
    buildTree: doBuildTree,
    getResultCount: doGetResultCount,
    groupByFile: doGroupByFile,

    stats(): CircuitStats {
      return { ...stats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('search-workspace', 'destroyed', 'Circuit destroyed', createdAt);
      return buildHealthReport('search-workspace', healthFromStats(stats), formatStatsDetails(stats), createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
